import React, { useState, useMemo, useEffect } from "react";
import { Plus, User, X, Send } from "lucide-react";
import { INK, INK_2, INK_3, LINE, LINE_2, BURGUNDY, BURGUNDY_TINT, BAD_INK } from "../../Bureau/bureauTokens";
import { httpClient } from "../../../HttpClient";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { normalizeText } from "../../../utils/text.js";

// .pl-legend-add — bouton "+" en pointillés : recherche libre parmi tous les
// collègues, pour DEMANDER l'accès au planning de quelqu'un. La liste des
// collaborateurs vient de EtatGlobal.js (déjà chargée globalement au login,
// voir fetchUsers) — filtrée localement par nom, sans réseau : un fetch
// /api/users dédié ici dupliquait cet appel à chaque montage et rendait la
// recherche perceptiblement lente à l'ouverture. Les collègues qui m'ont
// DÉJÀ partagé leur planning n'apparaissent plus ici : ils sont affichés
// directement en pastille par Planning.jsx (voir excludeIds) — ce bouton ne
// sert donc plus qu'à envoyer une nouvelle demande (email avec lien
// Accepter/Refuser, voir planningShareRequestController.js), pas à ajouter
// directement.
function AddCollaboratorButton({ excludeIds, onRequestSent }) {
  const { users: allUsers } = useEtatGlobal();
  const [sentIds, setSentIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [confirmUser, setConfirmUser] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    httpClient
      .get("/api/planning-share-requests/sent")
      .then(({ data }) => setSentIds(new Set((data.data || []).map((r) => r.ownerId))))
      .catch((err) => console.error("Erreur lors de la récupération des demandes envoyées :", err));
  }, []);

  const matches = useMemo(() => {
    const excluded = new Set(excludeIds || []);
    const query = normalizeText(q);
    return (allUsers || [])
      .filter((u) => !excluded.has(u.id))
      .filter((u) => !query || normalizeText(`${u.name} ${u.lastName}`).includes(query))
      .slice(0, 8);
  }, [allUsers, excludeIds, q]);

  const pick = (u) => {
    setConfirmUser(u);
    setError(null);
    setOpen(false);
    setQ("");
  };

  const confirmRequest = async () => {
    if (!confirmUser) return;
    setSending(true);
    setError(null);
    try {
      await httpClient.post("/api/planning-share-requests", { ownerId: confirmUser.id });
      setSentIds((prev) => new Set(prev).add(confirmUser.id));
      onRequestSent && onRequestSent(confirmUser);
      setConfirmUser(null);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi de la demande.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title="Demander l'accès au planning d'un collègue"
        className="flex items-center justify-center"
        style={{ width: 26, height: 26, borderRadius: "50%", border: `1px dashed ${LINE_2}`, color: INK_3, background: "#fff" }}
      >
        <Plus size={13} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-full mt-1 z-20 overflow-hidden"
            style={{ width: 240, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, boxShadow: "0 10px 28px rgba(0,0,0,.12)" }}
          >
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un collègue…"
              className="w-full h-8 px-2.5 text-xs outline-none"
              style={{ border: 0, borderBottom: `1px solid ${LINE}`, color: INK_2 }}
            />
            <div className="max-h-56 overflow-y-auto">
              {matches.map((u) => {
                const alreadySent = sentIds.has(u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => !alreadySent && pick(u)}
                    disabled={alreadySent}
                    className="w-full flex items-center gap-2 text-left text-xs px-3 py-2 disabled:cursor-default"
                    style={{ color: alreadySent ? INK_3 : INK }}
                  >
                    <User size={12} style={{ color: INK_3 }} /> {u.name} {u.lastName}
                    {alreadySent && (
                      <span className="ml-auto text-[10px] flex-shrink-0" style={{ color: INK_3 }}>
                        Demande envoyée
                      </span>
                    )}
                  </button>
                );
              })}
              {matches.length === 0 && (
                <div className="text-[11px] px-3 py-2" style={{ color: INK_3 }}>
                  Aucun résultat
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {confirmUser && (
        <div
          className="fixed inset-0 z-[220] flex items-center justify-center p-4"
          style={{ background: "rgba(31,22,32,.40)" }}
          onClick={() => !sending && setConfirmUser(null)}
        >
          <div
            className="w-full max-w-sm flex flex-col gap-3.5 px-6 py-6"
            style={{ background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(20,14,24,.30)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 40, height: 40, borderRadius: "50%", background: BURGUNDY_TINT, color: BURGUNDY }}
              >
                <Send size={17} />
              </div>
              <button
                onClick={() => setConfirmUser(null)}
                disabled={sending}
                className="flex items-center justify-center"
                style={{ color: INK_3 }}
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-[13px]" style={{ color: INK }}>
              Vous allez demander à <strong>{confirmUser.name} {confirmUser.lastName}</strong> d'avoir accès à son planning.
            </p>
            <p className="text-xs" style={{ color: INK_3 }}>
              Un email lui sera envoyé. Vous serez informé(e) par email dès que la demande aura été acceptée ou refusée.
            </p>
            {error && (
              <p className="text-xs" style={{ color: BAD_INK }}>
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-1">
              <button
                onClick={() => setConfirmUser(null)}
                disabled={sending}
                className="px-3.5 py-2 text-xs"
                style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK }}
              >
                Annuler
              </button>
              <button
                onClick={confirmRequest}
                disabled={sending}
                className="px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-60"
                style={{ background: BURGUNDY, borderRadius: 10 }}
              >
                {sending ? "Envoi…" : "Valider"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCollaboratorButton;
